import { Microscope, FileCheck2, QrCode, FlaskConical, Filter, ShieldCheck, Package, Truck } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { motion } from "framer-motion";
import ScientificPattern from "@/components/ScientificPattern";

const STEPS = [
  { icon: FlaskConical, label: "Manufacturing", desc: "Peptides are synthesized under controlled conditions using validated synthesis protocols." },
  { icon: Filter, label: "Purification", desc: "Compounds undergo multi-stage purification to remove synthesis byproducts and impurities." },
  { icon: Microscope, label: "Testing", desc: "Independent labs perform HPLC and mass spectrometry analysis on every production batch." },
  { icon: ShieldCheck, label: "Quality Approval", desc: "Batches must meet our 99%+ purity standard before release for distribution." },
  { icon: Package, label: "Packaging", desc: "Approved compounds are lyophilized and sealed in tamper-evident research packaging." },
  { icon: Truck, label: "Shipping", desc: "Orders ship in insulated packaging engineered to preserve compound stability in transit." },
];

export default function LabTesting() {
  usePageMeta("Laboratory Testing", "Every batch is independently tested via HPLC and mass spectrometry with a batch-specific Certificate of Analysis.", "/lab-testing");
  return (
    <div data-testid="lab-testing-page">
      <section className="relative py-24 bg-section-tint text-center overflow-hidden">
        <ScientificPattern variant="hex" className="opacity-50" />
        <div className="relative max-w-3xl mx-auto px-6">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600">Laboratory Testing</span>
          <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-medium text-slate-900 font-display">Independent <span className="text-gradient-blue">Verification</span>, Every Batch</h1>
          <p className="mt-6 text-base text-slate-600 leading-relaxed">
            Every compound we manufacture is analyzed by accredited third-party laboratories using HPLC and mass spectrometry to confirm purity, identity, and structural integrity.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
        {[
          { icon: Microscope, title: "HPLC Testing", desc: "High-performance liquid chromatography confirms compound purity down to sub-percentage precision." },
          { icon: FileCheck2, title: "Certificate of Analysis", desc: "Every batch is issued a COA documenting purity percentage, test date, and lab reference." },
          { icon: QrCode, title: "QR Verification", desc: "Scan the QR code on any product label to instantly access that batch's testing documentation." },
        ].map((f) => (
          <div key={f.title} className="border border-slate-200 rounded-2xl p-6 card-lift hover:border-blue-200 hover:shadow-blue-glow" data-testid={`lab-testing-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-900 to-blue-500 flex items-center justify-center mb-4 shadow-blue-glow-sm">
              <f.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
            </div>
            <h3 className="font-display font-semibold text-slate-900 mb-2">{f.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-center text-3xl font-display font-medium text-slate-900 mb-16">Our Manufacturing & Testing Process</h2>
          <div className="relative flex flex-col md:flex-row items-center md:items-start justify-between gap-10">
            <div className="hidden md:block absolute top-7 left-0 right-0 h-px bg-slate-200" />
            {STEPS.map((step, idx) => (
              <motion.div key={step.label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }} className="group relative flex flex-col items-center text-center flex-1 max-w-[160px]">
                <div className="h-14 w-14 rounded-full bg-white border-2 border-brandblue-600 flex items-center justify-center relative z-10 shadow-sm group-hover:shadow-blue-glow group-hover:scale-110 transition-all duration-300">
                  <step.icon className="h-6 w-6 text-brandblue-600" strokeWidth={1.5} />
                </div>
                <p className="mt-4 text-sm font-semibold text-slate-900">{step.label}</p>
                <p className="text-xs text-slate-500 mt-1">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
